'use client';
// LES CHAMPS GUIDÉS D'UNE MAINTENANCE (RQ, 03/11/2026) — communs à la saisie
// (`SaisirMaintenance`), à la correction (`CorrigerIntervention`) et, pour la
// durée d'arrêt, aux rubriques de panne d'une NC.
//
//  · Prestataire : les sociétés que la fiche de l'appareil connaît.
//  · Fait par / remis en service par : les personnes HABILITÉES sur l'appareil
//    (`GET ged/equip/:id/habilitations`), plus — pour « fait par » — les
//    techniciens du service technique.
//  · Durée d'arrêt : un nombre et une unité, ou « sans arrêt ».
//  · Pièces changées : des pièces une à une, suggérées d'après celles déjà
//    changées sur l'appareil, ou « aucune ».
//  · Vérification avant remise en service : des cases, et une précision.
//
// La valeur enregistrée reste un TEXTE (voir `lib/champs-maintenance.ts`) :
// une valeur ancienne hors liste est montrée et gardée, jamais effacée.
import React, { useEffect, useState } from 'react';
import { inputStyle, I } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import {
  lireDuree, ecrireDuree, DUREE_VIDE, type Duree,
  lirePieces, ecrirePieces, clePiece,
  lireVerification, ecrireVerification, VERIFICATIONS,
} from '@/lib/champs-maintenance';
import type { GedEquipement } from '@/lib/types';

export const AIDE_CHAMP: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', marginTop: 3 };

/* ───────────────────────────── Personnes habilitées ───────────────────── */

const NIVEAUX: Record<string, { rang: number; label: string }> = {
  routine: { rang: 1, label: 'routine' }, avancee: { rang: 2, label: 'avancé' }, expert: { rang: 3, label: 'expert' },
};
export type Habilite = { orgId: string; nom: string; niveau: string };

/** `undefined` = lecture en cours ; `null` = illisible ; sinon la liste, une
 *  personne une seule fois, à son plus haut niveau. */
export function useHabilites(documentId: string | undefined): Habilite[] | null | undefined {
  const [lu, setLu] = useState<{ id: string; liste: Habilite[] | null } | null>(null);
  useEffect(() => {
    if (!documentId) return;
    let vivant = true;
    void fetch(`/api/ged/ged/equip/${encodeURIComponent(documentId)}/habilitations`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { habilites?: Record<string, { orgId: string; nom: string }[]> } | null) => {
        if (!vivant) return;
        if (!j) { setLu({ id: documentId, liste: null }); return; }
        const parPersonne = new Map<string, Habilite>();
        for (const [niveau, gens] of Object.entries(j.habilites || {})) {
          for (const g of gens) {
            const cle = g.orgId || g.nom;
            const avant = parPersonne.get(cle);
            if (g.nom && (!avant || (NIVEAUX[niveau]?.rang ?? 0) > (NIVEAUX[avant.niveau]?.rang ?? 0))) {
              parPersonne.set(cle, { orgId: g.orgId, nom: g.nom, niveau });
            }
          }
        }
        setLu({ id: documentId, liste: [...parPersonne.values()].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')) });
      })
      .catch(() => { if (vivant) setLu({ id: documentId, liste: null }); });
    return () => { vivant = false; };
  }, [documentId]);
  return !documentId ? [] : lu?.id === documentId ? lu.liste : undefined;
}

export function optionsHabilites(h: Habilite[] | null | undefined) {
  return (h ?? []).map((x) => ({ valeur: x.nom, libelle: `${x.nom} (${NIVEAUX[x.niveau]?.label ?? x.niveau})` }));
}

export function etatHabilites(h: Habilite[] | null | undefined): string {
  return h === undefined ? 'Lecture des habilitations…'
    : h === null ? 'Les habilitations n’ont pas pu être lues.'
      : h.length === 0 ? 'Personne n’est habilité sur cet appareil : à poser depuis sa fiche (Habilitations).' : '';
}

/* ──────────────────────── Sociétés et techniciens de la fiche ─────────── */

/** Les sociétés que la fiche connaît, chacune avec son rôle ; une société à
 *  deux titres ne s'affiche qu'une fois (le premier rôle gagne). */
export function societesDe(a: GedEquipement | null | undefined) {
  const l: { valeur: string; libelle: string }[] = [];
  for (const [nom, role] of [
    [a?.contrat?.societe, 'contrat de maintenance'],
    [a?.contactTechnique?.societe, 'service technique'],
    [a?.fournisseur, 'fournisseur de l’appareil'],
  ] as const) {
    const n = (nom || '').trim();
    if (n && !l.some((x) => x.valeur.toLowerCase() === n.toLowerCase())) l.push({ valeur: n, libelle: `${n} — ${role}` });
  }
  return l;
}

export function techniciensPrestataire(a: GedEquipement | null | undefined) {
  return (a?.contactTechnique?.techniciens ?? [])
    .map((t) => (t.nom || '').trim()).filter(Boolean)
    .map((n) => ({ valeur: n, libelle: `${n} — ${a?.contactTechnique?.societe || 'prestataire'}` }));
}

/** Une liste qui garde une valeur déjà saisie hors liste (un nom d'avant) :
 *  l'ouvrir ne doit jamais effacer ce qui était écrit. */
export function ChoixListe({ id, valeur, onChange, vide, groupes }: {
  id: string; valeur: string; onChange: (v: string) => void; vide: string;
  groupes: { titre: string; options: { valeur: string; libelle: string }[] }[];
}) {
  const connues = groupes.flatMap((g) => g.options.map((o) => o.valeur));
  return (
    <select id={id} value={valeur} onChange={(ev) => onChange(ev.target.value)} style={inputStyle}>
      <option value="">{vide}</option>
      {valeur && !connues.includes(valeur) && <option value={valeur}>{valeur} (saisie d’origine)</option>}
      {groupes.filter((g) => g.options.length).map((g) => (
        <optgroup key={g.titre} label={g.titre}>
          {g.options.map((o) => <option key={`${g.titre}-${o.valeur}`} value={o.valeur}>{o.libelle}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

/* ─────────────────────────────── Durée d'arrêt ─────────────────────────── */

export function ChampDuree({ id, valeur, onChange }: { id: string; valeur: string; onChange: (v: string) => void }) {
  const lu = lireDuree(valeur);
  /* Un état LOCAL : « 1, » en cours de frappe, ou l'unité choisie avant le
     nombre, ne s'écrivent pas encore en texte — relus depuis la valeur, ils
     disparaîtraient sous les doigts. On ne se recale sur la valeur que si
     elle change d'ailleurs (Annuler, autre fiche).
     Un texte ancien qui ne se relit pas : les cases partent vides, le texte
     reste affiché — et enregistré tel quel tant qu'on ne saisit rien. */
  const [d, setD] = useState<Duree>(lu.duree ?? { ...DUREE_VIDE });
  const [vu, setVu] = useState(valeur);
  if (vu !== valeur) {
    setVu(valeur);
    if (ecrireDuree(d) !== valeur) setD(lu.duree ?? { ...DUREE_VIDE });
  }
  const poser = (x: Partial<Duree>) => {
    const n = { ...d, ...x };
    setD(n);
    const texte = ecrireDuree(n);
    setVu(texte);
    onChange(texte);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input id={id} inputMode="decimal" value={d.sansArret ? '' : d.nombre} disabled={d.sansArret}
          onChange={(ev) => poser({ nombre: ev.target.value.replace(/[^\d.,]/g, ''), sansArret: false })}
          aria-label="Durée d’arrêt — nombre" style={{ ...inputStyle, width: 70 }} />
        <select value={d.unite} disabled={d.sansArret} onChange={(ev) => poser({ unite: ev.target.value as Duree['unite'] })}
          aria-label="Durée d’arrêt — unité" style={{ ...inputStyle, width: 'auto' }}>
          <option value="h">heure(s)</option>
          <option value="j">jour(s)</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={d.sansArret} onChange={(ev) => poser({ sansArret: ev.target.checked, nombre: ev.target.checked ? '' : d.nombre })}
            style={{ accentColor: 'var(--color-primary)' }} />
          sans arrêt
        </label>
      </div>
      {!lu.duree && <div style={AIDE_CHAMP}>Saisie d’origine : « {lu.brut} »</div>}
    </div>
  );
}

/* ─────────────────────────────── Pièces changées ───────────────────────── */

export function ChampPieces({ id, valeur, onChange, connues }: {
  id: string; valeur: string; onChange: (v: string) => void;
  /** Les pièces déjà changées sur l'appareil (`piecesConnues`). */
  connues: string[];
}) {
  const { aucune, pieces } = lirePieces(valeur);
  const [saisie, setSaisie] = useState('');
  const ajouter = () => {
    const p = saisie.trim();
    if (!p) return;
    if (!pieces.some((q) => clePiece(q) === clePiece(p))) onChange(ecrirePieces(false, [...pieces, p]));
    setSaisie('');
  };
  const proposees = connues.filter((c) => !pieces.some((p) => clePiece(p) === clePiece(c)));
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
        <input type="checkbox" checked={aucune} onChange={(ev) => onChange(ev.target.checked ? ecrirePieces(true, []) : '')}
          style={{ accentColor: 'var(--color-primary)' }} />
        Aucune pièce changée
      </label>
      {!aucune && (
        <>
          {pieces.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {pieces.map((p) => (
                <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, padding: '3px 4px 3px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)' }}>
                  {p}
                  <Bouton taille="sm" variante="discret" aria-label={`Retirer ${p}`}
                    onClick={() => onChange(ecrirePieces(false, pieces.filter((q) => q !== p)))}>
                    <I n="x" s={11} />
                  </Bouton>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input id={id} list={`${id}-connues`} value={saisie} onChange={(ev) => setSaisie(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); ajouter(); } }}
              onBlur={ajouter}
              placeholder={proposees.length ? 'Choisir ou écrire une pièce' : 'Nom ou référence de la pièce'} style={inputStyle} />
            <datalist id={`${id}-connues`}>
              {proposees.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          {proposees.length > 0 && <div style={AIDE_CHAMP}>Proposées : les pièces déjà changées sur cet appareil.</div>}
        </>
      )}
    </div>
  );
}

/* ──────────────────── Vérification avant remise en service ─────────────── */

export function ChampVerification({ id, valeur, onChange }: { id: string; valeur: string; onChange: (v: string) => void }) {
  const { cochees, precision } = lireVerification(valeur);
  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
      <legend style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Vérification faite avant la remise en service
      </legend>
      <div style={{ display: 'flex', gap: '6px 16px', flexWrap: 'wrap' }}>
        {VERIFICATIONS.map((v) => (
          <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={cochees.includes(v)}
              onChange={(ev) => onChange(ecrireVerification(ev.target.checked ? [...cochees, v] : cochees.filter((x) => x !== v), precision))}
              style={{ accentColor: 'var(--color-primary)' }} />
            {v}
          </label>
        ))}
      </div>
      <input id={id} value={precision} onChange={(ev) => onChange(ecrireVerification(cochees, ev.target.value))}
        placeholder="Précision (autre contrôle, résultat, lot de CQ…)" aria-label="Précision sur la vérification" style={inputStyle} />
    </fieldset>
  );
}
